/**
 * Basket API Utilities
 * Handles integration with external shopping basket API for pricing and discount validation
 */

export interface BasketApiConfig {
    endpoint: string;
    headers?: Record<string, string>;
    timeout?: number;
    retries?: number;
}

export interface BasketApiPayload {
    cart: {
        token: string;
        validate_only: string;
        customer: {
            email: string;
            custcode: string;
        };
        line_items: {
            line_items: Array<{
                name: string;
                sku: string;
                price: number;
                compare_price: number | null;
                quantity: number;
                unique_id: string;
            }>;
        };
        discounts_applied: {
            discounts_applied: any[];
        };
        giftcards_applied: {
            giftcards_applied: any[];
        };
    };
}

export interface BasketApiResponse {
    success: boolean;
    cart?: {
        cartCId: number;
        cart: {
            unique_id: string;
            customer: {
                email: string;
                custcode: string;
                store_number?: string | null;
            };
            new_discount_or_gc?: {
                number: string;
                pin: string;
                type: string;
                status_code: string;
                status_message: string;
            };
            line_items: {
                line_items: Array<{
                    id: string;
                    unique_id: string;
                    sku: string;
                    rrp_price: string;
                    compare_price: string;
                    web_price: string;
                    new_price?: string;
                    quantity: string;
                    discount_applied_number?: string;
                    message?: string;
                    members_pricing_flag?: boolean;
                    discounts_applied?: {
                        discount_codes: Array<{
                            discount_code: string;
                            discount_value: string;
                            status_message: string;
                        }>;
                    };
                }>;
            };
            discounts_applied: {
                discounts_applied: any[];
            };
            giftcards_applied: {
                giftcards_applied: any[];
            };
        };
    };
    payload?: any;
    // Legacy fields for backwards compatibility
    line_items?: Array<{
        variant_id: string;
        new_price?: number;
        properties?: Record<string, any>;
    }>;
    discount_applications?: Array<{
        code: string;
        success: boolean;
        message?: string;
        amount?: number;
    }>;
    gift_card_applications?: Array<{
        code: string;
        success: boolean;
        message?: string;
        amount?: number;
    }>;
    applied_discounts?: any[];
    applied_gift_cards?: any[];
}

/**
 * Get current basket API configuration
 */
export const getBasketApiConfig = (): BasketApiConfig => {
    const config = {
        endpoint: window.basketApiConfig?.endpoint || '/api/shopping-basket',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...(window.basketApiConfig?.headers || {})
        },
        timeout: window.basketApiConfig?.timeout || 10000,
        retries: window.basketApiConfig?.retries || 2
    };

    // Add middleware API key header if configured
    if (window.basketApiConfig?.middlewareApiKey) {
        config.headers['middleware-api-key'] = window.basketApiConfig.middlewareApiKey;
    }

    return config;
};

/**
 * Get current cart data from Shopify
 */
export const getCurrentCartData = async () => {
    const cartResponse = await fetch('/cart.js');
    if (!cartResponse.ok) {
        throw new Error('Failed to fetch current cart data');
    }
    return await cartResponse.json();
};

/**
 * Get customer information if available
 */
export const getCustomerInfo = (customerId?: string) => {
    if (window.customerData || customerId) {
        return {
            id: customerId || window.customerData?.id,
            email: window.customerData?.email,
            phone: window.customerData?.phone
        };
    }
    return null;
};

/**
 * Prepare basket API payload from current cart state
 */
export const prepareBasketApiPayload = async (
    triggerType: 'cart_changed' | 'discount_submit' | 'gift_card_submit',
    options: {
        discountCode?: string;
        giftCardCode?: string;
        customerId?: string;
    } = {}
): Promise<BasketApiPayload> => {
    const cart = await getCurrentCartData();
    // Compose customer info for payload
    const customer = {
        email: '',
        custcode: ''
    };
    // Compose line_items array
    const line_items = cart.items.map(item => ({
        name: item.product_title || item.title || '',
        sku: item.sku || item.variant_id?.toString() || '',
        price: item.price / 100,
        compare_price: item.compare_at_price ? item.compare_at_price / 100 : null,
        quantity: item.quantity,
        unique_id: item.sku || item.variant_id?.toString() || ''
    }));
    // Compose payload
    const payload: BasketApiPayload = {
        cart: {
            token: cart.token,
            validate_only: 'true',
            customer,
            line_items: { line_items },
            discounts_applied: { discounts_applied: [] },
            giftcards_applied: { giftcards_applied: [] }
        }
    };
    return payload;
};

/**
 * Call the basket API with retry logic
 */
export const callBasketApi = async (
    payload: BasketApiPayload,
    config: BasketApiConfig = getBasketApiConfig()
): Promise<BasketApiResponse> => {
    let lastError: Error | null = null;
    const retries = config.retries ?? 2;

    for (let attempt = 0; attempt <= retries; attempt++) {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), config.timeout);

            const response = await fetch(config.endpoint, {
                method: 'POST',
                headers: config.headers,
                body: JSON.stringify(payload),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Basket API request failed: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            lastError = error instanceof Error ? error : new Error(String(error));
            console.warn(`Basket API attempt ${attempt + 1} failed:`, error);

            if (attempt < retries) {
                // Wait before retrying (exponential backoff)
                await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
            }
        }
    }

    throw lastError || new Error('Basket API request failed');
};

/**
 * Apply basket API updates to the cart
 */
export const applyBasketApiUpdates = async (basketResult: BasketApiResponse) => {
    let cartUpdateNeeded = false;

    // --- Handle new nested response structure ---
    if (
        basketResult &&
        basketResult.cart &&
        basketResult.cart.cart &&
        basketResult.cart.cart.line_items &&
        basketResult.cart.cart.line_items.line_items
    ) {
        // Get current Shopify cart
        const shopifyCart = await getCurrentCartData();
        const responseLineItems = basketResult.cart.cart.line_items.line_items;

        // If no line items are returned, do not clear the cart
        if (!responseLineItems || responseLineItems.length === 0) {
            console.warn('No line items returned from basket API, skipping cart clear.');
            return false;
        }

        // Clear the current cart first
        try {
            await fetch('/cart/clear.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
        } catch (error) {
            console.error('Failed to clear cart:', error);
            return false;
        }

        let allUpdatesOk = true;

        // Rebuild cart with the exact structure from basket API
        for (const lineItem of responseLineItems) {
            // Find the original Shopify item to get variant_id
            const originalShopifyItem = shopifyCart.items.find(i => {
                const variantIdMatch = i.variant_id?.toString() === lineItem.unique_id?.toString() || i.variant_id?.toString() === lineItem.sku?.toString();
                const skuMatch = i.sku && (i.sku.toString() === lineItem.sku?.toString() || i.sku.toString() === lineItem.unique_id?.toString());
                return variantIdMatch || skuMatch;
            });

            if (!originalShopifyItem) {
                console.warn('Could not find original Shopify item for basket API line item:', lineItem);
                allUpdatesOk = false;
                continue;
            }

            // Prepare properties for this line item
            let properties = {};

            if (lineItem.discounts_applied?.discount_codes && lineItem.discounts_applied.discount_codes.length > 0) {
                // Convert new_price to cents (string)
                let newPriceCents = '';
                if (lineItem.new_price) {
                    newPriceCents = Math.round(parseFloat(lineItem.new_price) * 100).toString();
                }

                // Set properties with discount information
                properties = {
                    ...(newPriceCents && { _new_price: newPriceCents }),
                    ...(lineItem.message && { _discount_message: String(lineItem.message) }),
                    ...(lineItem.discount_applied_number && { _discount_applied_number: String(lineItem.discount_applied_number) }),
                    ...(lineItem.id && { _basket_line_id: String(lineItem.id) }),
                };
            }

            // Ensure all property values are strings
            Object.keys(properties).forEach(key => {
                if (typeof properties[key] !== 'string') {
                    properties[key] = String(properties[key]);
                }
            });

            try {
                // Add this line item to the cart
                const addResponse = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        id: originalShopifyItem.variant_id,
                        quantity: parseInt(lineItem.quantity),
                        properties: properties
                    })
                });

                if (!addResponse.ok) {
                    const errorData = await addResponse.json().catch(() => ({}));
                    console.error(`Failed to add line item ${lineItem.id}:`, errorData);
                    allUpdatesOk = false;
                } else {
                    cartUpdateNeeded = true;
                }
            } catch (error) {
                console.error(`Error adding line item ${lineItem.id}:`, error);
                allUpdatesOk = false;
            }
        }

        // Only refresh cart once, after all updates
        if (cartUpdateNeeded && allUpdatesOk) {
            try {
                const refreshResponse = await fetch('/cart.js');
                if (refreshResponse.ok) {
                    const updatedCart = await refreshResponse.json();
                    window.dispatchEvent(new CustomEvent('cart:updated', {
                        detail: {
                            cart: updatedCart,
                            action: 'basket_api_update'
                        }
                    }));
                }
            } catch (error) {
                console.warn('Failed to refresh cart after basket API updates:', error);
            }
        } else if (!allUpdatesOk) {
            console.error('Some line items failed to update, cart may be in inconsistent state');
        }
    }

    return cartUpdateNeeded;
};

/**
 * Process basket API response and handle discount/gift card applications
 */
export const processBasketApiResponse = (basketResult: BasketApiResponse) => {
    const processedResult: {
        priceUpdates: Array<{
            variant_id: string;
            new_price?: number;
            properties?: Record<string, any>;
        }>;
        discountApplications: Array<{
            code: string;
            success: boolean;
            message?: string;
            amount?: number;
        }>;
        giftCardApplications: Array<{
            code: string;
            success: boolean;
            message?: string;
            amount?: number;
        }>;
        errors: string[];
    } = {
        priceUpdates: [],
        discountApplications: [],
        giftCardApplications: [],
        errors: []
    };

    // Process price updates
    if (basketResult.line_items) {
        processedResult.priceUpdates = basketResult.line_items.filter(item => item.new_price);
    }

    // Process discount applications
    if (basketResult.discount_applications) {
        processedResult.discountApplications = basketResult.discount_applications;

        // Log successful applications
        basketResult.discount_applications.forEach(discount => {
            if (discount.success && discount.code) {
                console.log(`Discount ${discount.code} applied successfully`);
            } else if (!discount.success) {
                console.warn(`Discount ${discount.code} failed: ${discount.message}`);
                processedResult.errors.push(`Discount ${discount.code}: ${discount.message}`);
            }
        });
    }

    // Process gift card applications
    if (basketResult.gift_card_applications) {
        processedResult.giftCardApplications = basketResult.gift_card_applications;

        // Log successful applications
        basketResult.gift_card_applications.forEach(giftCard => {
            if (giftCard.success && giftCard.code) {
                console.log(`Gift card ${giftCard.code} applied successfully`);
            } else if (!giftCard.success) {
                console.warn(`Gift card ${giftCard.code} failed: ${giftCard.message}`);
                processedResult.errors.push(`Gift card ${giftCard.code}: ${giftCard.message}`);
            }
        });
    }

    return processedResult;
};

/**
 * Main basket API sync function
 */
export const syncWithBasketApi = async (
    triggerType: 'cart_changed' | 'discount_submit' | 'gift_card_submit',
    options: {
        discountCode?: string;
        giftCardCode?: string;
        customerId?: string;
    } = {}
) => {
    try {
        // Prepare payload
        const payload = await prepareBasketApiPayload(triggerType, options);

        // Call basket API
        const basketResult = await callBasketApi(payload);

        // Process response
        const processedResult = processBasketApiResponse(basketResult);

        // Apply updates to cart
        const cartUpdateNeeded = await applyBasketApiUpdates(basketResult);

        return {
            success: true,
            basketResult,
            processedResult,
            cartUpdateNeeded
        };
    } catch (error) {
        console.error('Error syncing with basket API:', error);
        throw error;
    }
};
